import { Link } from "react-router-dom";
import { ArrowRight, Smartphone, RefreshCw, LayoutGrid, Users, Gift, Globe, Truck, Package } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { mediaUrl, euros } from "@/lib/api";
import Reveal from "@/components/Reveal";

const APP_FEATURES = [
  { icon: RefreshCw, label: "+1000 questions régulièrement mises à jour" },
  { icon: LayoutGrid, label: "Multi-catégories — ville, football, Aiglons, culture" },
  { icon: Users, label: "Multi-joueurs avec scores automatiques" },
  { icon: Gift, label: "Gratuite pour tous les acheteurs du jeu" },
  { icon: Globe, label: "Accessible partout — web, mobile, tablette" },
];

export function usePrices() {
  const { pricing } = useSite();
  const promo = pricing?.promo;
  const promoOn = !!promo?.active;
  return {
    price1: promoOn && promo.price_1 ? promo.price_1 : pricing?.price_1 ?? 34.9,
    price2: promoOn && promo.price_2 ? promo.price_2 : pricing?.price_2 ?? 59,
    base1: pricing?.price_1 ?? 34.9,
    base2: pricing?.price_2 ?? 59,
    compareAt2: pricing?.compare_at_2 ?? 69.8,
    promoLabel: promoOn ? promo.label : "",
  };
}

export default function AppOffers() {
  const { content, settings } = useSite();
  const { price1, price2, compareAt2, promoLabel } = usePrices();

  return (
    <>
      <section data-testid="app-section" className="py-16 sm:py-24 lg:py-32 bg-sand">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <a href={settings?.app_url || "#"} target="_blank" rel="noopener noreferrer" data-testid="app-preview-link">
              <img src={mediaUrl("/api/uploads/app-phone-bg.png")} alt="Aperçu de l'application compagnon Douzième Homme"
                className="w-full max-w-sm mx-auto hover:-translate-y-1 transition-transform" loading="lazy" />
            </a>
          </Reveal>
          <Reveal className="lg:col-span-7 order-1 lg:order-2" delay={0.1}>
            <p className="eyebrow mb-4">{content.app_eyebrow}</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase leading-[1.1] text-onyx">
              {content.app_title}
            </h2>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl">{content.app_text}</p>
            <ul className="mt-8 space-y-3" data-testid="app-features-list">
              {APP_FEATURES.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-3 text-sm sm:text-base font-medium text-onyx">
                  <Icon className="w-5 h-5 text-rouge shrink-0" /> {label}
                </li>
              ))}
            </ul>
            <a href={settings?.app_url || "#"} target="_blank" rel="noopener noreferrer" data-testid="app-cta-access"
              className="mt-8 inline-flex items-center gap-2 bg-onyx text-cream px-8 py-4 text-sm font-bold uppercase tracking-wide hover:bg-rouge transition-colors">
              <Smartphone className="w-4 h-4" /> Accéder à l'application
            </a>
          </Reveal>
        </div>
      </section>

      <section data-testid="offers-section" className="py-16 sm:py-24 lg:py-32">
        <div className="container-x">
          <Reveal className="text-left sm:text-center mb-12 sm:mb-16">
            <p className="eyebrow mb-4">Choisissez votre formule</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase leading-[1.1] text-onyx">
              Une boîte. Deux. À vous de jouer.
            </h2>
            {promoLabel && <p className="mt-4 inline-block bg-gold/20 text-onyx text-sm font-bold px-4 py-1.5" data-testid="promo-label">{promoLabel}</p>}
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-testid="offers-grid">
            <Reveal>
              <div className="h-full bg-white border border-line p-8 sm:p-10 flex flex-col hover:border-rouge/40 transition-colors">
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-onyx">1 boîte</h3>
                <p className="text-sm text-muted-foreground mt-2">Pour essayer en famille ou se faire plaisir.</p>
                <p className="mt-6 font-display text-4xl sm:text-5xl font-extrabold text-onyx" data-testid="offer-price-1">{euros(price1)}</p>
                <ul className="mt-6 space-y-2.5 text-sm text-muted-foreground flex-1">
                  <li className="flex gap-2"><Truck className="w-4 h-4 text-rouge shrink-0 mt-0.5" /> Livré sous 48h en point relais</li>
                  <li className="flex gap-2"><ArrowRight className="w-4 h-4 text-rouge shrink-0 mt-0.5" /> Retrait gratuit possible à Nice</li>
                </ul>
                <Link to="/acheter?offre=1" data-testid="offer-cta-1"
                  className="mt-8 block text-center border-2 border-onyx text-onyx px-6 py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-onyx hover:text-cream transition-colors">
                  Commander
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="relative h-full bg-onyx text-cream p-8 sm:p-10 flex flex-col border border-onyx">
                <span className="absolute -top-3 left-8 bg-rouge text-white text-xs font-bold uppercase tracking-wide px-3 py-1">Le plus choisi</span>
                <h3 className="font-display text-xl font-bold uppercase tracking-tight">2 boîtes</h3>
                <p className="text-sm text-cream/60 mt-2">Une pour vous, une pour offrir.</p>
                <p className="mt-6 flex items-baseline gap-3">
                  <span className="font-display text-4xl sm:text-5xl font-extrabold" data-testid="offer-price-2">{euros(price2)}</span>
                  <span className="text-lg text-cream/50 line-through" data-testid="offer-compare-2">{euros(compareAt2)}</span>
                </p>
                <p className="mt-1 text-sm font-bold text-gold">Économisez {euros(compareAt2 - price2)}</p>
                <ul className="mt-6 space-y-2.5 text-sm text-cream/70 flex-1">
                  <li className="flex gap-2"><Package className="w-4 h-4 text-rouge-light shrink-0 mt-0.5" /> 2 boîtes regroupées dans un seul envoi</li>
                  <li className="flex gap-2"><ArrowRight className="w-4 h-4 text-rouge-light shrink-0 mt-0.5" /> Livré sous 48h</li>
                </ul>
                <Link to="/acheter?offre=2" data-testid="offer-cta-2"
                  className="mt-8 block text-center bg-rouge text-white px-6 py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
                  Commander
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
